import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiInfo,
  FiUsers,
  FiFileText,
  FiSave,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiArrowLeft,
  FiEdit2,
  FiChevronDown,
  FiChevronUp,
  FiImage,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Header from "../components/layout/Header";
import JuliaPanelAside from "../components/layout/JuliaPanelAside";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import client from "../api/client";
import SubModuleCard from "../components/ui/SubModuleCard";
import { BudgetRH } from "../types";

// ── Types ───────────────────────────────────────────────────────────────────

interface StrategieItem {
  titre: string;
  description: string;
}
interface Dirigeant {
  nom: string;
  role: string;
}
interface Pole {
  nom: string;
  attendus: string;
}
interface Accord {
  id: number;
  titre: string;
  type_accord: string;
  file_name: string;
  notes: string;
  created_at: string;
}

interface OrgNode {
  id: string;
  nom: string;
  poste: string;
  children: OrgNode[];
}

interface CompanyData {
  name: string;
  siret: string;
  sector: string;
  address: string;
  city: string;
  presentation: string;
  valeurs: string[];
  dirigeants: Dirigeant[];
  strategie_court_terme: StrategieItem[];
  strategie_moyen_terme: StrategieItem[];
  strategie_long_terme: StrategieItem[];
  effectif: number;
  etp: number | null;
  ccn_code: string;
  ccn_nom: string;
  logiciel_paie: string;
  organigramme: any;
  attendus_poles: Pole[];
  mutuelle: string;
  prevoyance: string;
  budget_rh: BudgetRH | null;
}

// ── Sous-module Mes informations ─────────────────────────────────────────────

const MesInformations: React.FC<{
  data: CompanyData;
  onSave: (d: Partial<CompanyData>) => void;
}> = ({ data, onSave }) => {
  const [form, setForm] = useState({
    ...data,
    valeurs: Array.isArray(data.valeurs) ? data.valeurs : [],
    dirigeants: Array.isArray(data.dirigeants) ? data.dirigeants : [],
    strategie_court_terme: Array.isArray(data.strategie_court_terme)
      ? data.strategie_court_terme
      : [],
    strategie_moyen_terme: Array.isArray(data.strategie_moyen_terme)
      ? data.strategie_moyen_terme
      : [],
    strategie_long_terme: Array.isArray(data.strategie_long_terme)
      ? data.strategie_long_terme
      : [],
  });
  const [newValeur, setNewValeur] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  // Charger le logo actuel
  useEffect(() => {
    client
      .get("/structure/logo/file", { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data as Blob);
        setLogoPreview(url);
      })
      .catch(() => {});
  }, []);

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await client.post("/structure/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
      toast.success("Logo mis à jour");
    } catch {
      toast.error("Erreur lors de l'upload du logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const addValeur = () => {
    if (newValeur.trim()) {
      setForm((f) => ({ ...f, valeurs: [...f.valeurs, newValeur.trim()] }));
      setNewValeur("");
    }
  };

  const removeValeur = (i: number) =>
    setForm((f) => ({
      ...f,
      valeurs: f.valeurs.filter((_, idx) => idx !== i),
    }));

  const addDirigeant = () =>
    setForm((f) => ({
      ...f,
      dirigeants: [...f.dirigeants, { nom: "", role: "" }],
    }));
  const updateDirigeant = (i: number, key: "nom" | "role", val: string) =>
    setForm((f) => ({
      ...f,
      dirigeants: f.dirigeants.map((d, idx) =>
        idx === i ? { ...d, [key]: val } : d,
      ),
    }));
  const removeDirigeant = (i: number) =>
    setForm((f) => ({
      ...f,
      dirigeants: f.dirigeants.filter((_, idx) => idx !== i),
    }));

  const addStrategie = (
    key:
      | "strategie_court_terme"
      | "strategie_moyen_terme"
      | "strategie_long_terme",
  ) =>
    setForm((f) => ({
      ...f,
      [key]: [...f[key], { titre: "", description: "" }],
    }));
  const updateStrategie = (
    key:
      | "strategie_court_terme"
      | "strategie_moyen_terme"
      | "strategie_long_terme",
    i: number,
    field: "titre" | "description",
    val: string,
  ) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    }));
  const removeStrategie = (
    key:
      | "strategie_court_terme"
      | "strategie_moyen_terme"
      | "strategie_long_terme",
    i: number,
  ) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    fontSize: "var(--font-size-sm)",
    fontFamily: "var(--font-family)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "var(--font-size-xs)",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.375rem",
    display: "block",
  };

  const StrategieBlock = ({
    title,
    colorHint,
    keyName,
  }: {
    title: string;
    colorHint: string;
    keyName:
      | "strategie_court_terme"
      | "strategie_moyen_terme"
      | "strategie_long_terme";
  }) => (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.75rem",
        }}
      >
        <span
          style={{
            fontSize: "var(--font-size-sm)",
            fontWeight: 600,
            color: colorHint,
          }}
        >
          {title}
        </span>
        <button
          onClick={() => addStrategie(keyName)}
          style={{
            fontSize: "var(--font-size-xs)",
            color: colorHint,
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
          }}
        >
          <FiPlus size={12} /> Ajouter
        </button>
      </div>
      {form[keyName].map((item, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: "0.5rem",
            marginBottom: "0.5rem",
            alignItems: "center",
          }}
        >
          <input
            style={inputStyle}
            placeholder="Idée clé / titre"
            value={item.titre}
            onChange={(e) =>
              updateStrategie(keyName, i, "titre", e.target.value)
            }
          />
          <input
            style={inputStyle}
            placeholder="Description de l'attendu"
            value={item.description}
            onChange={(e) =>
              updateStrategie(keyName, i, "description", e.target.value)
            }
          />
          <button
            onClick={() => removeStrategie(keyName, i)}
            style={{
              color: "var(--color-danger)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.25rem",
            }}
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      ))}
      {form[keyName].length === 0 && (
        <p
          style={{
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-muted)",
            fontStyle: "italic",
          }}
        >
          Aucun objectif défini
        </p>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Logo et visuel */}
      <Card header={<span>Logo et visuel de l'entreprise</span>}>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: "var(--radius-lg)",
              border: "2px dashed var(--color-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              background: "var(--color-bg)",
              flexShrink: 0,
            }}
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <FiImage
                size={32}
                style={{ color: "var(--color-text-muted)", opacity: 0.4 }}
              />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
                margin: "0 0 0.5rem",
              }}
            >
              {logoPreview ? "Logo actuel" : "Aucun logo configuré"}
            </p>
            <p
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-secondary)",
                margin: "0 0 1rem",
              }}
            >
              Formats acceptés : PNG, JPG, SVG, WebP. Le logo sera affiché dans
              la barre de navigation et les documents générés.
            </p>
            <input
              ref={logoInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.webp"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleLogoUpload(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              icon={<FiUpload size={14} />}
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              {uploadingLogo
                ? "Envoi en cours..."
                : logoPreview
                  ? "Modifier le logo"
                  : "Ajouter un logo"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Identité */}
      <Card header={<span>Identité de la structure</span>}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
          }}
        >
          <div>
            <label style={labelStyle}>Nom de l'entreprise</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label style={labelStyle}>SIRET</label>
            <input
              style={inputStyle}
              value={form.siret || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, siret: e.target.value }))
              }
            />
          </div>
          <div>
            <label style={labelStyle}>Secteur d'activité</label>
            <input
              style={inputStyle}
              value={form.sector || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, sector: e.target.value }))
              }
            />
          </div>
          <div>
            <label style={labelStyle}>Ville</label>
            <input
              style={inputStyle}
              value={form.city || ""}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Adresse</label>
            <input
              style={inputStyle}
              value={form.address || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Effectif */}
      <Card header={<span>Effectif</span>}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
          }}
        >
          <div>
            <label style={labelStyle}>Nombre de salariés (effectif)</label>
            <input
              style={inputStyle}
              type="number"
              value={form.effectif || 0}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  effectif: parseInt(e.target.value) || 0,
                }))
              }
            />
            <p
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-muted)",
                marginTop: "0.25rem",
              }}
            >
              Indicatif uniquement : ne crée pas de salarié dans le registre
            </p>
          </div>
          <div>
            <label style={labelStyle}>ETP (Équivalent Temps Plein)</label>
            <input
              style={inputStyle}
              type="number"
              step="0.5"
              value={form.etp || ""}
              placeholder="ex : 3.5"
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  etp: parseFloat(e.target.value) || null,
                }))
              }
            />
            <p
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-muted)",
                marginTop: "0.25rem",
              }}
            >
              Ex : 5 salariés mais 3,5 ETP si certains sont à temps partiel
            </p>
          </div>
        </div>
      </Card>

      {/* Présentation */}
      <Card header={<span>Présentation de l'entreprise</span>}>
        <textarea
          style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
          placeholder="Décrivez votre entreprise, son histoire, ses produits/services..."
          value={form.presentation || ""}
          onChange={(e) =>
            setForm((f) => ({ ...f, presentation: e.target.value }))
          }
        />
      </Card>

      {/* Valeurs */}
      <Card header={<span>Valeurs de l'entreprise</span>}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "1rem",
          }}
        >
          {form.valeurs.map((v, i) => (
            <span
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                padding: "0.375rem 0.75rem",
                borderRadius: "var(--radius-full)",
                background: "var(--color-primary)15",
                color: "var(--color-primary)",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
              }}
            >
              {v}
              <button
                onClick={() => removeValeur(i)}
                style={{
                  color: "var(--color-primary)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <input
            style={{ ...inputStyle }}
            placeholder="Ajouter une valeur"
            value={newValeur}
            onChange={(e) => setNewValeur(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addValeur()}
          />
          <Button onClick={addValeur} icon={<FiPlus size={14} />}>
            Ajouter
          </Button>
        </div>
      </Card>

      {/* Dirigeants */}
      <Card
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Dirigeants / Conseil d'administration</span>
            <Button
              onClick={addDirigeant}
              icon={<FiPlus size={14} />}
              variant="outline"
            >
              Ajouter
            </Button>
          </div>
        }
      >
        {form.dirigeants.length === 0 && (
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
            }}
          >
            Aucun dirigeant renseigné
          </p>
        )}
        {form.dirigeants.map((d, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: "0.75rem",
              marginBottom: "0.75rem",
              alignItems: "center",
            }}
          >
            <input
              style={inputStyle}
              placeholder="Nom et prénom"
              value={d.nom}
              onChange={(e) => updateDirigeant(i, "nom", e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="Rôle / fonction"
              value={d.role}
              onChange={(e) => updateDirigeant(i, "role", e.target.value)}
            />
            <button
              onClick={() => removeDirigeant(i)}
              style={{
                color: "var(--color-danger)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        ))}
      </Card>

      {/* Stratégie */}
      <Card header={<span>Stratégie d'entreprise</span>}>
        <div
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          <StrategieBlock
            title="Court terme (1 à 3 ans)"
            colorHint="var(--color-success)"
            keyName="strategie_court_terme"
          />
          <hr
            style={{
              border: "none",
              borderTop: "1px solid var(--color-border-light)",
            }}
          />
          <StrategieBlock
            title="Moyen terme (3 à 5 ans)"
            colorHint="var(--color-warning)"
            keyName="strategie_moyen_terme"
          />
          <hr
            style={{
              border: "none",
              borderTop: "1px solid var(--color-border-light)",
            }}
          />
          <StrategieBlock
            title="Long terme (5 à 7 ans)"
            colorHint="var(--color-primary)"
            keyName="strategie_long_terme"
          />
        </div>
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button icon={<FiSave size={16} />} onClick={() => onSave(form)}>
          Enregistrer les informations
        </Button>
      </div>
    </div>
  );
};

// ── Composant Organigramme ──────────────────────────────────────────────────

const generateId = () => Math.random().toString(36).slice(2, 10);

/** Garantit children[] sur chaque nœud (évite le crash page blanche). */
const normalizeOrgNode = (raw: unknown): OrgNode => {
  const fallback: OrgNode = {
    id: generateId(),
    nom: "",
    poste: "Direction générale",
    children: [],
  };
  if (!raw || typeof raw !== "object") return fallback;
  const node = raw as Partial<OrgNode> & { children?: unknown };
  const childrenRaw = Array.isArray(node.children) ? node.children : [];
  return {
    id: typeof node.id === "string" && node.id ? node.id : generateId(),
    nom: typeof node.nom === "string" ? node.nom : "",
    poste: typeof node.poste === "string" ? node.poste : "",
    children: childrenRaw.map((child) => normalizeOrgNode(child)),
  };
};

const ORG_LINE = "var(--color-border)";
const ORG_LINE_W = 2;
const ORG_GAP_Y = 28;
const ORG_GAP_X = 28;

const OrgNodeComponent: React.FC<{
  node: OrgNode;
  onUpdate: (id: string, field: "nom" | "poste", value: string) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (id: string) => void;
  level: number;
}> = ({ node, onUpdate, onAddChild, onDelete, level }) => {
  const children = Array.isArray(node.children) ? node.children : [];
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(!node.nom && !node.poste);
  const showChildren = !collapsed && children.length > 0;

  const inputStyle: React.CSSProperties = {
    padding: "0.3rem 0.5rem",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-sm)",
    fontSize: "var(--font-size-xs)",
    fontFamily: "var(--font-family)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const colors = [
    "var(--color-primary)",
    "var(--color-secondary)",
    "#0284C7",
    "var(--color-success)",
    "var(--color-warning)",
  ];
  const nodeColor = colors[level % colors.length];

  const hLineStyle = (
    index: number,
    total: number,
  ): React.CSSProperties | null => {
    if (total <= 1) return null;
    const isFirst = index === 0;
    const isLast = index === total - 1;
    return {
      position: "absolute",
      top: 0,
      height: ORG_LINE_W,
      background: ORG_LINE,
      left: isFirst ? "50%" : 0,
      right: isLast ? "50%" : 0,
    };
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "max-content",
        maxWidth: "100%",
      }}
    >
      {/* Carte du poste */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          background: "var(--color-surface)",
          border: `2px solid ${nodeColor}40`,
          borderTop: `3px solid ${nodeColor}`,
          borderRadius: "var(--radius-md)",
          padding: "0.7rem 0.85rem",
          minWidth: 168,
          maxWidth: 220,
          boxShadow: "0 2px 8px rgba(15, 28, 51, 0.06)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {editing ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
          >
            <input
              style={inputStyle}
              placeholder="Nom / titre"
              value={node.nom}
              onChange={(e) => onUpdate(node.id, "nom", e.target.value)}
              autoFocus
            />
            <input
              style={inputStyle}
              placeholder="Poste / fonction"
              value={node.poste}
              onChange={(e) => onUpdate(node.id, "poste", e.target.value)}
            />
            <button
              type="button"
              onClick={() => setEditing(false)}
              style={{
                alignSelf: "flex-end",
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#fff",
                background: nodeColor,
                border: "none",
                borderRadius: "var(--radius-sm)",
                cursor: "pointer",
                padding: "0.2rem 0.55rem",
              }}
            >
              Valider
            </button>
          </div>
        ) : (
          <div>
            <p
              style={{
                fontWeight: 600,
                fontSize: "var(--font-size-sm)",
                margin: 0,
                color: nodeColor,
              }}
            >
              {node.nom || "Sans nom"}
            </p>
            <p
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-secondary)",
                margin: "0.15rem 0 0",
              }}
            >
              {node.poste || "Sans poste"}
            </p>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "0.2rem",
            marginTop: "0.5rem",
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            title="Modifier"
            onClick={() => setEditing(true)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--color-text-muted)",
              padding: "0.15rem",
            }}
          >
            <FiEdit2 size={12} />
          </button>
          <button
            type="button"
            title="Ajouter un poste subordonné"
            onClick={() => onAddChild(node.id)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: nodeColor,
              padding: "0.15rem",
            }}
          >
            <FiPlus size={13} />
          </button>
          {level > 0 && (
            <button
              type="button"
              title="Supprimer"
              onClick={() => onDelete(node.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--color-danger)",
                padding: "0.15rem",
              }}
            >
              <FiTrash2 size={12} />
            </button>
          )}
          {children.length > 0 && (
            <button
              type="button"
              title={collapsed ? "Déplier" : "Replier"}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--color-text-muted)",
                padding: "0.15rem",
              }}
            >
              {collapsed ? (
                <FiChevronDown size={12} />
              ) : (
                <FiChevronUp size={12} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Branche vers les enfants */}
      {showChildren && (
        <>
          {/* Trait vertical sous le parent */}
          <div
            style={{
              width: ORG_LINE_W,
              height: ORG_GAP_Y / 2,
              background: ORG_LINE,
              flexShrink: 0,
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "max-content",
            }}
          >
            {children.map((child, index) => {
              const line = hLineStyle(index, children.length);
              const sidePad = ORG_GAP_X / 2;
              return (
                <div
                  key={child.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    paddingTop: ORG_GAP_Y / 2,
                    /* Padding symétrique : le centre (50%) = centre de la carte */
                    paddingLeft: sidePad,
                    paddingRight: sidePad,
                  }}
                >
                  {/* Segment horizontal (centre → voisins) */}
                  {line && <div aria-hidden style={line} />}
                  {/* Trait vertical vers la carte enfant */}
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: ORG_LINE_W,
                      height: ORG_GAP_Y / 2,
                      background: ORG_LINE,
                    }}
                  />
                  <OrgNodeComponent
                    node={child}
                    onUpdate={onUpdate}
                    onAddChild={onAddChild}
                    onDelete={onDelete}
                    level={level + 1}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const OrganigrammeEditor: React.FC<{
  organigramme: OrgNode | null;
  onSave: (data: OrgNode) => void;
}> = ({ organigramme, onSave }) => {
  const [tree, setTree] = useState<OrgNode>(() =>
    normalizeOrgNode(organigramme),
  );

  const updateNode = useCallback(
    (id: string, field: "nom" | "poste", value: string) => {
      const update = (node: OrgNode): OrgNode => {
        const kids = Array.isArray(node.children) ? node.children : [];
        if (node.id === id) return { ...node, [field]: value, children: kids };
        return { ...node, children: kids.map(update) };
      };
      setTree((prev) => update(prev));
    },
    [],
  );

  const addChild = useCallback((parentId: string) => {
    const add = (node: OrgNode): OrgNode => {
      const kids = Array.isArray(node.children) ? node.children : [];
      if (node.id === parentId) {
        return {
          ...node,
          children: [
            ...kids,
            { id: generateId(), nom: "", poste: "", children: [] },
          ],
        };
      }
      return { ...node, children: kids.map(add) };
    };
    setTree((prev) => add(prev));
  }, []);

  const deleteNode = useCallback((id: string) => {
    const remove = (node: OrgNode): OrgNode => {
      const kids = Array.isArray(node.children) ? node.children : [];
      return {
        ...node,
        children: kids.filter((c) => c.id !== id).map(remove),
      };
    };
    setTree((prev) => remove(prev));
  }, []);

  return (
    <Card
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Organigramme de la structure</span>
          <Button icon={<FiSave size={14} />} onClick={() => onSave(tree)}>
            Enregistrer
          </Button>
        </div>
      }
    >
      <p
        style={{
          fontSize: "var(--font-size-xs)",
          color: "var(--color-text-secondary)",
          marginBottom: "1rem",
        }}
      >
        Construisez l&apos;arborescence hiérarchique. Utilisez + pour ajouter un
        poste rattaché ; les traits se recentrent automatiquement.
      </p>
      <div
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          padding: "1.25rem 1rem",
          background:
            "linear-gradient(180deg, rgba(14,165,233,0.04) 0%, transparent 100%)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--color-border-light)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minWidth: "min-content",
            width: "100%",
          }}
        >
          <OrgNodeComponent
            node={tree}
            onUpdate={updateNode}
            onAddChild={addChild}
            onDelete={deleteNode}
            level={0}
          />
        </div>
      </div>
    </Card>
  );
};

// ── Sous-module Anticiper sa gestion du personnel ────────────────────────────

const GestionPersonnel: React.FC<{
  data: CompanyData;
  onSave: (d: any) => void;
}> = ({ data, onSave }) => {
  const [form, setForm] = useState({
    ccn_code: data.ccn_code || "",
    ccn_nom: data.ccn_nom || "",
    ccn_mode: data.ccn_nom ? "has_ccn" : "",
    logiciel_paie: data.logiciel_paie || "",
    mutuelle: data.mutuelle || "",
    prevoyance: data.prevoyance || "",
    attendus_poles: Array.isArray(data.attendus_poles)
      ? data.attendus_poles
      : ([] as Pole[]),
  });

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    fontSize: "var(--font-size-sm)",
    fontFamily: "var(--font-family)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "var(--font-size-xs)",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.375rem",
    display: "block",
  };

  const addPole = () =>
    setForm((f) => ({
      ...f,
      attendus_poles: [...f.attendus_poles, { nom: "", attendus: "" }],
    }));
  const updatePole = (i: number, key: "nom" | "attendus", val: string) =>
    setForm((f) => ({
      ...f,
      attendus_poles: f.attendus_poles.map((p, idx) =>
        idx === i ? { ...p, [key]: val } : p,
      ),
    }));
  const removePole = (i: number) =>
    setForm((f) => ({
      ...f,
      attendus_poles: f.attendus_poles.filter((_, idx) => idx !== i),
    }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* CCN */}
      <Card header={<span>Convention collective nationale (CCN)</span>}>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <button
            onClick={() => setForm((f) => ({ ...f, ccn_mode: "has_ccn" }))}
            style={{
              flex: 1,
              padding: "0.75rem",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              border: `2px solid ${form.ccn_mode === "has_ccn" ? "var(--color-primary)" : "var(--color-border)"}`,
              background:
                form.ccn_mode === "has_ccn"
                  ? "var(--color-primary)10"
                  : "transparent",
              fontWeight: 500,
              fontSize: "var(--font-size-sm)",
              color:
                form.ccn_mode === "has_ccn"
                  ? "var(--color-primary)"
                  : "var(--color-text)",
            }}
          >
            J'ai une CCN
          </button>
          <button
            onClick={() => setForm((f) => ({ ...f, ccn_mode: "no_ccn" }))}
            style={{
              flex: 1,
              padding: "0.75rem",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              border: `2px solid ${form.ccn_mode === "no_ccn" ? "var(--color-secondary)" : "var(--color-border)"}`,
              background:
                form.ccn_mode === "no_ccn"
                  ? "var(--color-secondary)10"
                  : "transparent",
              fontWeight: 500,
              fontSize: "var(--font-size-sm)",
              color:
                form.ccn_mode === "no_ccn"
                  ? "var(--color-secondary)"
                  : "var(--color-text)",
            }}
          >
            Julia m'aide à trouver la CCN idéale
          </button>
        </div>
        {form.ccn_mode === "has_ccn" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: "1rem",
            }}
          >
            <div>
              <label style={labelStyle}>Code IDCC</label>
              <input
                style={inputStyle}
                placeholder="ex : 1486"
                value={form.ccn_code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ccn_code: e.target.value }))
                }
              />
            </div>
            <div>
              <label style={labelStyle}>Nom de la convention</label>
              <input
                style={inputStyle}
                placeholder="ex : Convention Syntec"
                value={form.ccn_nom}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ccn_nom: e.target.value }))
                }
              />
            </div>
          </div>
        )}
        {form.ccn_mode === "no_ccn" && (
          <div
            style={{
              padding: "1rem",
              background: "var(--color-secondary)10",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            JULIA analysera votre secteur et vos informations pour vous proposer
            la convention collective la plus adaptée. Complétez d'abord les
            informations de votre structure, puis utilisez le chat JULIA pour
            obtenir une recommandation.
          </div>
        )}
      </Card>

      {/* Logiciel de paie */}
      <Card header={<span>Logiciel de paie</span>}>
        <label style={labelStyle}>Logiciel de paie utilisé</label>
        <input
          style={inputStyle}
          placeholder="ex : Silae, Sage, ADP, Payfit..."
          value={form.logiciel_paie}
          onChange={(e) =>
            setForm((f) => ({ ...f, logiciel_paie: e.target.value }))
          }
        />
        <p
          style={{
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-muted)",
            marginTop: "0.375rem",
          }}
        >
          Permet de créer le bon fichier de suivi des éléments variables de paie
          (EVP)
        </p>
      </Card>

      {/* Mutuelle & prévoyance */}
      <Card header={<span>Mutuelle & Prévoyance</span>}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
          }}
        >
          <div>
            <label style={labelStyle}>Mutuelle d'entreprise</label>
            <input
              style={inputStyle}
              placeholder="Nom de l'organisme"
              value={form.mutuelle}
              onChange={(e) =>
                setForm((f) => ({ ...f, mutuelle: e.target.value }))
              }
            />
          </div>
          <div>
            <label style={labelStyle}>Prévoyance</label>
            <input
              style={inputStyle}
              placeholder="Nom de l'organisme"
              value={form.prevoyance}
              onChange={(e) =>
                setForm((f) => ({ ...f, prevoyance: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Organigramme */}
      <OrganigrammeEditor
        organigramme={data.organigramme}
        onSave={(orgData) => onSave({ ...form, organigramme: orgData })}
      />

      {/* Attendus des pôles */}
      <Card
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Attendus par pôle / BU</span>
            <Button
              onClick={addPole}
              icon={<FiPlus size={14} />}
              variant="outline"
            >
              Ajouter un pôle
            </Button>
          </div>
        }
      >
        {(form.attendus_poles?.length ?? 0) === 0 && (
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
            }}
          >
            Aucun pôle défini. Ajoutez les pôles/BU de votre structure pour
            suivre leurs objectifs.
          </p>
        )}
        {(form.attendus_poles || []).map((pole, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr auto",
              gap: "0.75rem",
              marginBottom: "0.75rem",
              alignItems: "center",
            }}
          >
            <input
              style={inputStyle}
              placeholder="Nom du pôle"
              value={pole.nom}
              onChange={(e) => updatePole(i, "nom", e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="Attendus / objectifs"
              value={pole.attendus}
              onChange={(e) => updatePole(i, "attendus", e.target.value)}
            />
            <button
              onClick={() => removePole(i)}
              style={{
                color: "var(--color-danger)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        ))}
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button icon={<FiSave size={16} />} onClick={() => onSave(form)}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

// ── Sous-module Mes accords employeurs ───────────────────────────────────────

const MesAccords: React.FC<{ companyId?: number }> = () => {
  const [accords, setAccords] = useState<Accord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newAccord, setNewAccord] = useState({
    titre: "",
    type_accord: "",
    notes: "",
  });
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    client
      .get("/structure/accords")
      .then((r) => setAccords((r.data as any).data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async () => {
    if (!newAccord.titre) {
      toast.error("Le titre est obligatoire");
      return;
    }
    const fd = new FormData();
    fd.append("titre", newAccord.titre);
    fd.append("type_accord", newAccord.type_accord);
    fd.append("notes", newAccord.notes);
    if (file) fd.append("file", file);
    try {
      const r = await client.post("/structure/accords", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAccords((a) => [(r.data as any).data, ...a]);
      setShowForm(false);
      setNewAccord({ titre: "", type_accord: "", notes: "" });
      setFile(null);
      toast.success("Accord ajouté");
    } catch {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Supprimer cet accord ?")) return;
    try {
      await client.delete(`/structure/accords/${id}`);
      setAccords((a) => a.filter((x) => x.id !== id));
      toast.success("Accord supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    fontSize: "var(--font-size-sm)",
    fontFamily: "var(--font-family)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "var(--font-size-xs)",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.375rem",
    display: "block",
  };

  if (loading)
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "var(--color-text-muted)",
        }}
      >
        Chargement...
      </div>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button icon={<FiPlus size={16} />} onClick={() => setShowForm(true)}>
          Ajouter un accord
        </Button>
      </div>

      {showForm && (
        <Card header={<span>Nouvel accord employeur</span>}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label style={labelStyle}>Titre de l'accord *</label>
                <input
                  style={inputStyle}
                  value={newAccord.titre}
                  onChange={(e) =>
                    setNewAccord((a) => ({ ...a, titre: e.target.value }))
                  }
                  placeholder="ex : Accord télétravail 2024"
                />
              </div>
              <div>
                <label style={labelStyle}>Type d'accord</label>
                <input
                  style={inputStyle}
                  value={newAccord.type_accord}
                  onChange={(e) =>
                    setNewAccord((a) => ({ ...a, type_accord: e.target.value }))
                  }
                  placeholder="ex : Accord d'entreprise, Charte..."
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                value={newAccord.notes}
                onChange={(e) =>
                  setNewAccord((a) => ({ ...a, notes: e.target.value }))
                }
                placeholder="Informations complémentaires..."
              />
            </div>
            <div>
              <label style={labelStyle}>Fichier (PDF, Word)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ fontSize: "var(--font-size-sm)" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button icon={<FiUpload size={14} />} onClick={handleUpload}>
                Enregistrer
              </Button>
            </div>
          </div>
        </Card>
      )}

      {accords.length === 0 && !showForm && (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "var(--color-text-muted)",
          }}
        >
          <FiFileText
            size={40}
            style={{ opacity: 0.3, marginBottom: "1rem" }}
          />
          <p
            style={{
              fontSize: "var(--font-size-base)",
              fontWeight: 500,
              marginBottom: "0.5rem",
            }}
          >
            Aucun accord enregistré
          </p>
          <p style={{ fontSize: "var(--font-size-sm)" }}>
            Ajoutez vos conventions, accords d'entreprise et chartes pour que
            JULIA puisse les consulter lors de ses analyses.
          </p>
        </div>
      )}

      {accords.map((accord) => (
        <Card key={accord.id}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "var(--radius-md)",
                  background: "var(--color-primary)15",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FiFileText
                  size={18}
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <div>
                <p
                  style={{
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {accord.titre}
                </p>
                {accord.type_accord && (
                  <p
                    style={{
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-muted)",
                      margin: "0.125rem 0 0",
                    }}
                  >
                    {accord.type_accord}
                  </p>
                )}
                {accord.file_name && (
                  <p
                    style={{
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-secondary)",
                      margin: "0.25rem 0 0",
                    }}
                  >
                    {accord.file_name}
                  </p>
                )}
                {accord.notes && (
                  <p
                    style={{
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-secondary)",
                      margin: "0.375rem 0 0",
                    }}
                  >
                    {accord.notes}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleDelete(accord.id)}
              style={{
                color: "var(--color-danger)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0.25rem",
              }}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── Page principale ──────────────────────────────────────────────────────────

type SubModule = "informations" | "gestion" | "accords" | null;

const hashToModule = (hash: string): SubModule => {
  switch (hash) {
    case "#structure":
    case "#gestion":
      return "gestion";
    case "#informations":
      return "informations";
    case "#accords":
      return "accords";
    default:
      return null;
  }
};

const moduleToHash = (mod: SubModule): string => {
  switch (mod) {
    case "gestion":
      return "#structure";
    case "informations":
      return "#informations";
    case "accords":
      return "#accords";
    default:
      return "";
  }
};

const StructurePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModule, setActiveModule] = useState<SubModule>(() =>
    hashToModule(location.hash),
  );
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Hash = source de vérité (sidebar + deep-link)
  useEffect(() => {
    if (location.pathname !== "/structure") return;
    setActiveModule(hashToModule(location.hash));
  }, [location.pathname, location.hash]);

  const openHub = () => {
    navigate({ pathname: "/structure", hash: "" }, { replace: false });
  };

  const openModule = (mod: SubModule) => {
    navigate({ pathname: "/structure", hash: moduleToHash(mod) });
  };

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    client
      .get("/structure")
      .then((r) => {
        const data = (r.data as { data?: CompanyData }).data ?? null;
        setCompanyData(data);
        if (!data) setLoadError(true);
      })
      .catch(() => {
        setCompanyData(null);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSaveInformations = async (data: Partial<CompanyData>) => {
    try {
      const r = await client.put("/structure/informations", data);
      setCompanyData((r.data as { data: CompanyData }).data);
      toast.success("Informations enregistrées");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleSaveGestion = async (data: Record<string, unknown>) => {
    try {
      const r = await client.put("/structure/gestion-personnel", data);
      setCompanyData((r.data as { data: CompanyData }).data);
      toast.success("Données de gestion enregistrées");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const subModules = [
    {
      id: "informations" as SubModule,
      icon: <FiInfo size={24} />,
      title: "Mes informations",
      description:
        "Logo, présentation, valeurs, dirigeants, stratégie court/moyen/long terme, effectif et ETP",
      color: "var(--color-primary)",
    },
    {
      id: "gestion" as SubModule,
      icon: <FiUsers size={24} />,
      title: "Anticiper ma gestion du personnel",
      description:
        "Convention collective, logiciel de paie, organigramme, attendus des pôles, mutuelle et prévoyance",
      color: "var(--color-secondary)",
    },
    {
      id: "accords" as SubModule,
      icon: <FiFileText size={24} />,
      title: "Mes accords employeurs",
      description:
        "Conventions, accords d'entreprise et chartes en PDF ou Word : consultés par JULIA dans ses analyses",
      color: "#0284C7",
    },
  ];

  const activeSubModule = subModules.find((s) => s.id === activeModule);

  const renderActiveModule = () => {
    if (loading) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "var(--color-text-muted)",
          }}
        >
          Chargement…
        </div>
      );
    }
    if (activeModule !== "accords" && (loadError || !companyData)) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "var(--color-text-secondary)",
          }}
        >
          <p style={{ margin: "0 0 0.75rem" }}>
            Impossible de charger les données de structure.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "var(--color-brand)",
              color: "#fff",
              border: "none",
              borderRadius: "var(--radius-md)",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontFamily: "var(--font-family)",
            }}
          >
            Réessayer
          </button>
        </div>
      );
    }
    switch (activeModule) {
      case "informations":
        return (
          <MesInformations
            data={companyData!}
            onSave={handleSaveInformations}
          />
        );
      case "gestion":
        return (
          <GestionPersonnel data={companyData!} onSave={handleSaveGestion} />
        );
      case "accords":
        return <MesAccords />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header
        title={
          activeModule
            ? `Ma structure : ${activeSubModule?.title}`
            : "Ma structure"
        }
        subtitle={
          activeModule
            ? undefined
            : "Paramétrez et pilotez les informations de votre entreprise"
        }
      />

      <div style={{ padding: "1.5rem 2rem" }}>
        {!activeModule ? (
          // Hub : split-screen modules + Julia
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: "1 1 320px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                maxWidth: 860,
              }}
            >
              {loading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "3rem",
                    color: "var(--color-text-muted)",
                  }}
                >
                  Chargement...
                </div>
              ) : (
                subModules.map((mod) => (
                  <SubModuleCard
                    key={mod.id}
                    icon={mod.icon}
                    title={mod.title}
                    description={mod.description}
                    color={mod.color}
                    onClick={() => openModule(mod.id)}
                  />
                ))
              )}
            </div>
            <JuliaPanelAside
              moduleName="Ma Structure"
              contextMessage="Si vous le souhaitez, nous pouvons reprendre la saisie des attendus par pôle. Vous pouvez également cliquer sur la cloche pour consulter les tâches en attente sur ce module."
              pendingTasks={2}
            />
          </div>
        ) : (
          // Vue sous-module actif
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: "1.5rem" }}>
                <button
                  onClick={openHub}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "var(--font-size-sm)",
                    color: "var(--color-text-secondary)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <FiArrowLeft size={16} /> Retour aux sous-modules
                </button>
              </div>
              {renderActiveModule()}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default StructurePage;
